<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold">
        Categories ({{ listMeta.total }})
      </v-toolbar-title>

      <v-spacer></v-spacer>
      <v-row align="center" style="max-width: 300px">
        <v-text-field
          v-if="!loading"
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search label or description"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn class="ml-5" icon @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>
      </template>
    </v-app-bar>

    <v-app-bar flat color="transparent" align="center">
      <v-row class="d-flex flex-row-reverse">
        <v-skeleton-loader
          type="chip"
          transition="fade-transition"
          v-if="loading"
        ></v-skeleton-loader>

        <router-link
          v-else
          :to="{ name: 'categories.details', params: { id: 'new' } }"
        >
          <v-btn class="pa-2" color="primary">New Category</v-btn>
        </router-link>
      </v-row>
    </v-app-bar>

    <div class="mt-2 mx-4" id="category-list">
      <v-simple-table>
        <thead>
          <tr>
            <th
              width="5%"
              class="text-left pa-0"
              @click="sortColumn"
              data-column="id"
            >
              ID
              <v-icon v-if="params.sort_by == `id`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="15%"
              class="text-left pa-0"
              @click="sortColumn"
              data-column="label"
            >
              Label
              <v-icon v-if="params.sort_by == `label`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="5%"
              class="text-left"
              @click="sortColumn"
              data-column="parent_id"
            >
              Parent
              <v-icon v-if="params.sort_by == `parent_id`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="5%"
              class="text-left"
              @click="sortColumn"
              data-column="type"
            >
              Type
              <v-icon v-if="params.sort_by == `type`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
            <th
              width="15%"
              class="text-left"
              @click="sortColumn"
              data-column="created_at"
            >
              Added date
              <v-icon v-if="params.sort_by == `created_at`">{{
                icons.selectedArrow
              }}</v-icon>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-show="!isLoadingPagination"
            v-for="category in categories"
            :key="category.id"
          >
            <td>
              {{ category.id }}
            </td>
            <td>
              <router-link
                :to="{
                  name: 'categories.details',
                  params: {
                    id: category.parent_id ? category.parent_id : category.id,
                  },
                }"
                >{{ category.label }}</router-link
              >
            </td>
            <td>{{ category.parent ? category.parent.label : '' }}</td>
            <td>{{ category.readableType }}</td>
            <td>{{ category.addedDate }}</td>
          </tr>

          <template v-if="loading || isLoadingPagination">
            <tr v-for="index in params.limit" :key="index + '-skeleton'">
              <td class="py-3 pl-0" width="5%">
                <v-skeleton-loader
                  class="avatar-loading--36"
                  type="avatar"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>

      <br />
      <v-card
        v-if="initialLoadingPage"
        class="d-flex justify-end mb-6"
        flat
        tile
        no-gutters
      >
        <v-select
          dense
          solo
          class="pagination-limit"
          :items="limits"
          label="Items per page"
          v-model="params.limit"
          @change="onSelectItem"
        ></v-select>

        <v-pagination
          v-model="page"
          :length="listMeta.last_page"
          :total-visible="10"
        ></v-pagination>
      </v-card>
      <v-skeleton-loader
        type="list-item"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" right>
      {{ snackbar.message }}
      <v-btn color="white" text @click="snackbar.show = false">Ok</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiArrowUpBold,
  mdiArrowDownBold,
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'

export default {
  name: 'CategoriesPage',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll,
  },

  components: {
    AppBarNavIcon,
  },

  data() {
    return {
      loading: false,
      initialLoadingPage: false,
      isLoadingPagination: false,
      showSearch: false,
      showProfile: false,
      selectedProfile: null,
      filter: {
        search: '',
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        arrowUp: mdiArrowUpBold,
        arrowDown: mdiArrowDownBold,
        selectedArrow: mdiArrowUpBold,
      },
      snackbar: {
        show: false,
        message: null,
        color: null,
      },
      params: {
        sort_direction: 'asc',
        sort_by: null,
        limit: 10,
      },
      page: 1,
      limits: [5, 10, 20, 30, 50],
    }
  },

  created() {
    this.clearCategories()
    this.fetchCategories()
  },

  computed: {
    ...mapState({
      categories: (state) => state.category.list,
      listMeta: (state) => state.category.listMeta,
    }),

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    },
  },

  methods: {
    ...mapActions({
      getCategories: 'category/getCategories',
      exportToExcel: 'category/exportToExcel',
    }),

    ...mapMutations({
      clearCategories: 'category/clearCategoryList',
    }),

    pageChanged(page) {
      this.fetchCategories(page)
    },
    onSelectItem() {
      this.clearCategories()
      this.fetchCategories(1)
    },
    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchCategories(this.listMeta.current_page + 1)
      }
    },
    sortColumn(e) {
      this.params.sort_direction =
        this.params.sort_direction === 'asc' ? 'desc' : 'asc'

      this.icons.selectedArrow =
        this.params.sort_direction === 'asc' ? mdiArrowUpBold : mdiArrowDownBold

      this.clearCategories()
      this.params.sort_by = e.target.getAttribute('data-column')
      this.fetchCategories()
    },
    search: debounce(function () {
      this.clearCategories()
      this.fetchCategories()
    }, 600),

    async fetchCategories(page = 1) {
      if (this.loading) return

      let params = this.params
      params.page = page

      if (this.filter.search) {
        params.search = this.filter.search
      } else {
        params.search = ''
      }

      if (!this.initialLoadingPage) {
        this.loading = true
      } else {
        this.isLoadingPagination = true
      }

      await this.getCategories(params)

      if (!this.initialLoadingPage) {
        this.loading = false
      } else {
        this.isLoadingPagination = false
      }

      this.initialLoadingPage = true
    },
    showProfileDialog(profile) {
      this.selectedProfile = profile
      this.showProfile = true
    },
    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    },
  },
  watch: {
    page() {
      this.pageChanged(this.page)
    },
  },
}
</script>
